import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError, BaseResponse } from '@utils/types/shared/base';
import { PlanBo } from '@utils/types/back-office/plans-bo';

export type ResellerPlansListResponse = BaseResponse<PlanBo[]>;

/**
 * Hook to fetch plans that are published to a specific reseller.
 * Used when creating a sub-organization under a reseller.
 *
 * Uses GET /api/v3/admin/customer/{reseller_id}/plans endpoint.
 */
export const useGetResellerPlansBo = (
  resellerId: number | undefined,
  searchQuery?: string
) => {
  const fetchPlans = async (): Promise<ResellerPlansListResponse> => {
    if (!resellerId) {
      return { result: [], count: 0, total_results: 0 };
    }

    // Build URL with reseller ID
    let url = `/api/v3/admin/customer/${resellerId}/plans?offset=0&limit=100`;

    // Add search filter if provided
    if (searchQuery?.trim()) {
      url += `&q=${encodeURIComponent(`sku:like:%${searchQuery.trim()}%`)}`;
    }

    const { data } = await Axios.get<ResellerPlansListResponse>(url);

    return data;
  };

  return useQuery<ResellerPlansListResponse, AxiosError<BaseError>>(
    ['resellerPlansBo', resellerId, searchQuery],
    fetchPlans,
    {
      enabled: !!resellerId,
      keepPreviousData: true,
      useErrorBoundary: false,
      staleTime: 30 * 1000 // 30 seconds
    }
  );
};
